<template>
  <div>
    <div v-if="isJapan" class="mb-5">
      <label for="japanAgreeSharePersonalData" class="flex items-center">
        <input
          id="japanAgreeSharePersonalData"
          v-model="privacy.japanAgreeSharePersonalData"
          type="checkbox"
          class="mr-2"
        />
        <span class="mb-1 block text-xs text-gray-600">
          {{ $t('form.agree-share-personal-data.first-part') }}
          <TheNuxtLink
            :to="privacyPolicyPath"
            :title="$t('form.agree-share-personal-data.privacy')"
            class="text-blue-400 hover:text-blue-500 hover:underline"
          >
            {{ $t('form.agree-share-personal-data.privacy') }}
          </TheNuxtLink>
          {{ $t('form.agree-share-personal-data.end-part') }}
          <sup class="top-0 text-sm font-bold text-red-400">*</sup>
        </span>
      </label>
      <span
        v-if="!privacy.japanAgreeSharePersonalData"
        class="mb-1 block pl-6 text-xs text-red-400"
      >
        {{ $t('form.error-required-message') }}
      </span>
    </div>

    <div v-if="isTurkey" class="mb-5">
      <label for="turkeyAgreeSharePersonalData" class="flex items-center">
        <input
          id="turkeyAgreeSharePersonalData"
          v-model="privacy.turkeyAgreeSharePersonalData"
          type="checkbox"
          class="mr-2"
        />
        <span class="mb-1 block text-xs text-gray-600">
          I have read
          <TheNuxtLink
            to="https://www.advantech.com.tr/legal/privacy#section11"
            class="text-blue-400 hover:text-blue-500"
            title="KVKK Clarification Text"
          >
            KVKK Clarification Text
          </TheNuxtLink>
          and I accept to share with you my personal data.
          <sup class="top-0 text-sm font-bold text-red-400">*</sup>
        </span>
      </label>
      <span
        v-if="!privacy.turkeyAgreeSharePersonalData"
        class="mb-1 block pl-6 text-xs text-red-400"
      >
        {{ $t('form.error-required-message') }}
      </span>
    </div>

    <div v-if="isKorea" class="mb-5">
      <div>
        <label class="flex items-baseline">
          <input v-model="koreaSelectAll" type="checkbox" class="mr-2" />
          <span class="mb-1 block text-xs text-gray-600">
            전체동의 (선택사항 포함)
          </span>
        </label>
      </div>

      <div>
        <label class="flex items-baseline">
          <input
            v-model="privacy.koreaAgreeAdvPolicy"
            type="checkbox"
            class="mr-2"
          />
          <span class="mb-1 block text-xs text-gray-600">
            어드밴텍 이용약관 동의
            <TheNuxtLink
              to="https://www.advantech.co.kr/legal/privacy"
              class="text-blue-400 hover:text-blue-500"
              title="전체보기"
            >
              전체보기
            </TheNuxtLink>
            <sup class="top-0 text-sm font-bold text-red-400">*</sup>
          </span>
        </label>
        <span
          v-if="!privacy.koreaAgreeAdvPolicy"
          class="mb-1 block pl-6 text-xs text-red-400"
        >
          {{ $t('form.error-required-message') }}
        </span>
      </div>

      <div>
        <label class="flex items-baseline">
          <input
            v-model="privacy.koreaAgreePersonalInfo"
            type="checkbox"
            class="mr-2"
          />
          <span class="mb-1 block text-xs text-gray-600">
            개인정보 수집 및 이용 동의
            <sup class="top-0 text-sm font-bold text-red-400">*</sup>
          </span>
        </label>
        <span
          v-if="!privacy.koreaAgreePersonalInfo"
          class="mb-1 block pl-6 text-xs text-red-400"
        >
          {{ $t('form.error-required-message') }}
        </span>
      </div>

      <table
        width="100%"
        class="element-table mt-3"
        border="0"
        cellpadding="0"
        cellspacing="0"
      >
        <thead>
          <tr>
            <th colspan="2" valign="top">구분</th>
            <th valign="top">수집항목</th>
            <th valign="top">이용 목적</th>
            <th valign="top">이용∙보유기간</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td rowspan="3" valign="top">1:1 문의</td>
            <td valign="top">
              <span style="word-break: keep-all">필수</span>
            </td>
            <td valign="top">이름, 이메일, 전화번호, 회사명, 국가, 상담내용</td>
            <td valign="top">고객의 문의 대응 및 분쟁해결 등을 위함</td>
            <td rowspan="2" valign="top">
              소비자의 불만 또는 분쟁처리에 관한 기록: 3년
            </td>
          </tr>
          <tr>
            <td valign="top">
              <span style="word-break: keep-all">선택</span>
            </td>
            <td valign="top">제품, 시리얼넘버</td>
            <td valign="top">효율적인 상담 대응</td>
          </tr>
          <tr>
            <td valign="top">
              <span style="word-break: keep-all">선택</span>
            </td>
            <td valign="top">뉴스레터 수신 동의 여부</td>
            <td valign="top">신규 서비스/상품, 할인 이벤트 안내</td>
            <td valign="top">법정 의무 보유 기간</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup lang="ts">
  interface IFormPrivacy {
    japanAgreeSharePersonalData: boolean;
    turkeyAgreeSharePersonalData: boolean;
    koreaAgreeAdvPolicy: boolean;
    koreaAgreePersonalInfo: boolean;
  }

  interface IProps {
    propsPrivacy: IFormPrivacy;
    countryName: string;
  }

  const props = withDefaults(defineProps<IProps>(), {
    propsPrivacy: () => ({
      japanAgreeSharePersonalData: false,
      turkeyAgreeSharePersonalData: false,
      koreaAgreeAdvPolicy: false,
      koreaAgreePersonalInfo: false,
    }),
    countryName: '',
  });

  const emits = defineEmits<{
    (e: 'update', value: IFormPrivacy): void;
    (e: 'valid', value: boolean): void;
  }>();

  const { isJP } = useLanguage();

  const privacy = ref<IFormPrivacy>({
    japanAgreeSharePersonalData: false,
    turkeyAgreeSharePersonalData: false,
    koreaAgreeAdvPolicy: false,
    koreaAgreePersonalInfo: false,
  });

  const koreaSelectAll = computed({
    get: (): boolean => {
      return (
        privacy.value.koreaAgreeAdvPolicy &&
        privacy.value.koreaAgreePersonalInfo
      );
    },
    set: (value: boolean) => {
      if (value) {
        privacy.value.koreaAgreeAdvPolicy = true;
        privacy.value.koreaAgreePersonalInfo = true;
      }
    },
  });

  const isJapan = computed(
    (): boolean => props.countryName.toLowerCase() === 'japan'
  );

  const isKorea = computed((): boolean => {
    const name: string = props.countryName.toLowerCase();
    return name === 'korea' || name === 'south korea';
  });

  const isTurkey = computed((): boolean => {
    const name: string = props.countryName.toLowerCase();
    return name === 'turkey' || name === 'turkiye';
  });

  const isValidPrivacy = computed((): boolean => {
    if (isJapan.value) {
      return privacy.value.japanAgreeSharePersonalData === true;
    }

    if (isTurkey.value) {
      return privacy.value.turkeyAgreeSharePersonalData === true;
    }

    if (isKorea.value) {
      return (
        privacy.value.koreaAgreeAdvPolicy === true &&
        privacy.value.koreaAgreePersonalInfo === true
      );
    }

    return true;
  });

  const privacyPolicyPath = computed((): string => {
    if (isJP.value) {
      return 'https://www.advantech.com/ja-jp/legal/privacy';
    }
    return 'https://www.advantech.com/en/legal/privacy';
  });

  watch(privacy, (value) => emits('update', value), { deep: true });

  watch(isValidPrivacy, (value) => emits('valid', value));

  onMounted(() => {
    Object.assign(privacy.value, props.propsPrivacy);
  });

  export type { IFormPrivacy };
</script>

<style lang="scss" scoped>
  .element-table {
    @apply w-full bg-white;

    td,
    th {
      @apply p-1 text-left align-top text-xs;
      border: 1px solid #cccccc;
    }

    th {
      background-color: #f2f2f2;
    }
  }
</style>
